/**
 * "Child started/ended internet" desktop notifications — the web app's
 * answer to the Android parent app's UnifiedPush notifications (see
 * `core/notifications/push/` there and docs/INTERNET_ACCESS.md's "Parent
 * push notifications" row).
 *
 * **Deliberately derived from the poll, not from a push subscription.**
 * The backend's `PushService` sends the plain, unencrypted flavour of the
 * UnifiedPush protocol — a bare POST of the JSON payload to the endpoint
 * (see its doc comment). A browser's Web Push endpoint will not accept
 * that: it requires a VAPID-signed, `aes128gcm`-encrypted payload, so
 * registering a browser endpoint against `POST /push-subscriptions` would
 * silently deliver nothing. Rather than change the backend's push
 * contract as a side effect of adding a web client, the web app notices
 * the same transitions itself: the dashboard already polls every child's
 * account every 5 seconds, and `onlineDeviceId` flipping to or from
 * `null` is exactly the "started"/"ended" event the push carries.
 *
 * The honest limitation, stated in `web/README.md` too: this only fires
 * while a dashboard tab is open. Background delivery with the tab closed
 * needs real Web Push (VAPID keys on the backend, a service worker here)
 * — a follow-up, not something to fake.
 *
 * The wording matches `pushTextFor` in
 * `backend/src/internet-access/internet-access.util.ts` so both clients say
 * the same thing.
 */

/** Mirrors the backend's `PushPayload`. */
export interface NotificationPayload {
  title: string;
  body: string;
}

export function internetStartedNotification(childName: string): NotificationPayload {
  return { title: 'Internet aktiviert', body: `${childName} hat das Internet aktiviert.` };
}

/**
 * The backend distinguishes "the child ended it" from "the balance ran
 * out"; a poll-derived observer only sees that the session closed, so
 * this stays deliberately neutral rather than guessing which it was.
 */
export function internetEndedNotification(childName: string): NotificationPayload {
  return { title: 'Internet beendet', body: `${childName} ist nicht mehr online.` };
}

function isSupported(): boolean {
  // Also false in an insecure context — the Notification API is
  // https/localhost only, same as the rest of this app's assumptions.
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * Asks once, on dashboard mount — the web analog of Android's
 * `POST_NOTIFICATIONS` request in `ParentDashboardScreen`. A refusal is
 * not an error: the dashboard's live numbers are the real feature, these
 * are an added heads-up.
 */
export async function requestNotificationPermission(): Promise<void> {
  if (!isSupported()) return;
  if (Notification.permission !== 'default') return;
  try {
    await Notification.requestPermission();
  } catch {
    // Some browsers reject rather than resolve "denied" — same outcome.
  }
}

export function showNotification({ title, body }: NotificationPayload): void {
  if (!isSupported() || Notification.permission !== 'granted') return;
  try {
    // `tag` collapses repeats of the same event for the same child, so a
    // flapping session cannot stack up a pile of identical toasts.
    new Notification(title, { body, tag: `${title}:${body}` });
  } catch {
    // Notification construction can throw where only the service-worker
    // form is allowed (notably Android Chrome). Non-fatal.
  }
}
